
import Vue, { PropType } from 'vue';
import { mapActions } from 'vuex';
import { FEEDBACK_REPORT_SHOTS_MAX } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';

// What the API takes as a screenshot (feedback_service.clean_shots, which checks the image itself)
const TYPES: string[] = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES: number = 4 * 1024 * 1024;

// Screenshots for a comment or a new report on the Feedback page, picked before they're sent:
// chosen, dropped on it, or pasted (the comment box and the new report's text call `add` with what
// was pasted). PNG, JPEG or WebP, at most 4 MB each and six in all, as restServer takes them.
// `value` is the files picked; it emits `input` with the new list.
export default Vue.extend({
	name: 'FeedbackShotPicker',
	props: {
		value: { type: Array as PropType<File[]>, default: () => [] },
		disabled: { type: Boolean, default: false },
		hint: { type: String, default: '' },
	},
	data () {
		return {
			max: FEEDBACK_REPORT_SHOTS_MAX,
			dragging: false,
			previews: [] as string[],
		};
	},
	watch: {
		value: {
			immediate: true,
			handler (files: File[]) {
				this.previews.forEach((url: string) => URL.revokeObjectURL(url));
				this.previews = files.map((file: File) => URL.createObjectURL(file));
			},
		},
	},
	beforeDestroy () {
		this.previews.forEach((url: string) => URL.revokeObjectURL(url));
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		choose () {
			const input = this.$refs.input as HTMLInputElement | undefined;
			if (input) input.click();
		},
		onChosen (e: Event) {
			const input = e.target as HTMLInputElement;
			this.add(Array.from(input.files || []));
			input.value = '';
		},
		onDrop (e: DragEvent) {
			this.dragging = false;
			if (!this.disabled) this.add(Array.from(e.dataTransfer?.files || []));
		},
		// Pictures pasted into a text box: the ones among what was pasted. Returns whether there were any.
		addFromPaste (e: ClipboardEvent): boolean {
			const files: File[] = Array.from(e.clipboardData?.files || []).filter((file: File) => file.type.startsWith('image/'));
			if (!files.length || this.disabled) return false;
			this.add(files.map((file: File, i: number) => (file.name && file.name !== 'image.png'
				? file
				: new File([file], `pasted-${Date.now()}-${i + 1}.${file.type.split('/')[1] || 'png'}`, { type: file.type }))));
			return true;
		},
		add (files: File[]) {
			const kept: File[] = [];
			for (const file of files) {
				if (!TYPES.includes(file.type)) {
					this.say(`${file.name} isn't a PNG, JPEG or WebP image`);
					continue;
				}
				if (file.size > MAX_BYTES) {
					this.say(`${file.name} is larger than 4 MB`);
					continue;
				}
				kept.push(file);
			}
			const room: number = this.max - this.value.length;
			if (kept.length > room) this.say(`At most ${this.max} screenshots: ${kept.length - room} left out`);
			if (kept.length && room > 0) this.$emit('input', [...this.value, ...kept.slice(0, room)]);
		},
		remove (index: number) {
			this.$emit('input', this.value.filter((_file: File, i: number) => i !== index));
		},
		say (message: string) {
			this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message, show: true });
		},
	},
});
